import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getSupabaseClient } from '../supabaseClient';
import { useBusinessType } from '../context/BusinessContext';
import { 
  MessageCircle, 
  Filter,
  ChevronRight,
  ChevronLeft,
  Loader,
  AlertCircle,
  Check,
  Clock,
  RefreshCw,
  User,
  Bot,
  ArrowUpDown
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import PageHeader from '../components/PageHeader';
import { useClickAway } from 'react-use';
import { Button } from '../components/ui/button';

// Elegant animations with cinematic quality
const pageVariants = {
  initial: { opacity: 0 },
  animate: { 
    opacity: 1,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.08
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const cardVariants = {
  initial: { opacity: 0, y: 15 },
  animate: { 
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const listItemVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1]
    }
  },
  hover: {
    x: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.03)',
    transition: {
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1]
    }
  },
  tap: {
    scale: 0.985,
    transition: {
      duration: 0.15
    }
  }
};

const buttonVariants = {
  rest: { scale: 1 },
  hover: { 
    scale: 1.03,
    transition: {
      duration: 0.2,
      ease: [0.22, 1, 0.36, 1]
    }
  },
  tap: { 
    scale: 0.97,
    transition: {
      duration: 0.1
    }
  }
};

// Configuration for different conversation statuses
const statusConfig = {
  active: {
    color: 'text-blue-600 border-blue-200',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-100',
    label: 'Active'
  },
  completed: {
    color: 'text-emerald-600 border-emerald-200',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-100',
    label: 'Completed'
  },
  pending: {
    color: 'text-amber-600 border-amber-200',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-100',
    label: 'Pending'
  }
};

const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';

  // Remove any non-digit characters first
  const cleaned = phoneNumber.replace(/\D/g, '');

  // Handle US/Canada numbers (with or without +1)
  if (cleaned.length === 10 || (cleaned.length === 11 && cleaned.startsWith('1'))) {
    const number = cleaned.length === 11 ? cleaned.slice(1) : cleaned;
    return `(${number.slice(0,3)}) ${number.slice(3,6)}-${number.slice(6)}`;
  }

  // Handle international numbers
  if (cleaned.length > 10) {
    // Extract country code (assume first 2-3 digits after any +1)
    const countryCode = cleaned.startsWith('1') ? cleaned.slice(1,3) : cleaned.slice(0,2);
    const remaining = cleaned.startsWith('1') ? cleaned.slice(3) : cleaned.slice(2);

    // Format remaining numbers
    return `+${countryCode} (${remaining.slice(0,3)}) ${remaining.slice(3,6)}-${remaining.slice(6)}`;
  }

  return phoneNumber; // Return original if we can't format it
};

const formatConversationDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  
  // Format time consistently
  const timeString = date.toLocaleString('en-US', { 
    hour: 'numeric', 
    minute: '2-digit', 
    hour12: true 
  }).toLowerCase();

  // Check if it's today (same year, month, and day)
  const isToday = date.getDate() === now.getDate() && 
                  date.getMonth() === now.getMonth() &&
                  date.getFullYear() === now.getFullYear();

  // If it's today
  if (isToday) {
    return timeString;
  }

  // If it's within the last 7 days
  const diffTime = now - date;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  if (diffDays < 7) {
    const dayOfWeek = date.toLocaleString('en-US', { weekday: 'long' });
    return `${dayOfWeek} at ${timeString}`;
  }

  // Otherwise show full date
  const monthDay = date.toLocaleString('en-US', { 
    month: 'long',
    day: 'numeric'
  });
  return `${monthDay} at ${timeString}`;
};

// Get the most appropriate display name for the contact
const getDisplayName = (thread) => {
  // If contact exists and has a name
  if (thread.contact?.name) {
    const name = thread.contact.name;
    
    // Check if name contains "customer" (case-insensitive)
    if (name.toLowerCase().includes('customer')) {
      // Extract everything between the first "(" and last ")"
      const start = name.indexOf('(');
      const end = name.lastIndexOf(')');
      
      if (start !== -1 && end !== -1 && end > start) {
        // Return the content between parentheses, removing the parentheses themselves
        return name.substring(start + 1, end).trim();
      }
      
      // If no parentheses found, fall back to phone number
      return formatPhoneNumber(thread.contact.phone_number || thread.from_number || '');
    }
    
    return name;
  }
  
  // Fallback to formatted phone number if no contact
  return formatPhoneNumber(thread.from_number || thread.contact?.phone_number || '');
};

// Get preview of the last message in the conversation
const getLastMessagePreview = (thread) => {
  if (!thread.last_message) return "No messages yet";
  
  // Truncate message if too long
  const maxLength = 50;
  if (thread.last_message.length > maxLength) {
    return `${thread.last_message.substring(0, maxLength)}...`;
  }
  return thread.last_message;
};

// Determine conversation status based on last activity
const getConversationStatus = (thread) => {
  if (!thread.updated_at) return 'pending';
  
  const lastUpdate = new Date(thread.updated_at);
  const now = new Date();
  const diffHours = Math.abs(now - lastUpdate) / 36e5; // hours
  
  if (diffHours < 24) return 'active';
  if (diffHours < 72) return 'pending';
  return 'completed';
};

const TextsListPage = () => {
  const navigate = useNavigate();
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);
  const filterDropdownRef = useRef(null);

  const [conversations, setConversations] = useState([]);
  const [allConversations, setAllConversations] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    date: 'all'
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalConversations, setTotalConversations] = useState(0);
  const conversationsPerPage = 10;
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [showAIConversationsOnly, setShowAIConversationsOnly] = useState(false);
  const [hideOneMessageThreads, setHideOneMessageThreads] = useState(false);
  const [sortDirection, setSortDirection] = useState('desc');

  // Close dropdown when clicking outside
  useClickAway(filterDropdownRef, () => {
    if (showFilterDropdown) setShowFilterDropdown(false);
  });

  // Initial data fetch
  useEffect(() => {
    fetchInitialData();
  }, [showAIConversationsOnly, sortDirection]);

  const fetchInitialData = async () => {
    try {
      setIsInitialLoading(true);
      setError(null);

      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      // First, get all locations that belong to this user's account
      const { data: userLocations, error: locationsError } = await supabase
        .from('vapi_location_settings')
        .select('id')
        .eq('account_id', user.id);

      if (locationsError) throw locationsError;
      
      // Extract just the location IDs
      const locationIds = userLocations?.map(loc => loc.id) || [];
      
      // If user has no locations, show empty state
      if (locationIds.length === 0) {
        setTotalConversations(0);
        setConversations([]);
        setAllConversations([]);
        setIsInitialLoading(false);
        return;
      }

      // Build the base query
      let query = supabase
        .from('conversation_threads')
        .select(`
          id,
          created_at,
          updated_at,
          contact_id,
          location_id,
          node,
          config,
          node_config,
          contact:contacts(id, name, phone_number),
          texts!left(
            id,
            from_number,
            to_number,
            content,
            cost,
            created_at
          )
        `, { count: 'exact' })
        .in('location_id', locationIds);
        
      // Filter for AI conversations if enabled
      if (showAIConversationsOnly) {
        query = query.not('config', 'is', null);
      }
        
      // Apply sorting
      query = query.order('updated_at', { ascending: sortDirection === 'asc' });
      
      // Limit initial load to improve performance
      query = query.limit(10);

      const { data: initialThreads, count, error } = await query;

      if (error) throw error;

      // Process threads to add last message info and message counts
      const processedThreads = initialThreads?.map(thread => {
        // Sort texts by creation date (descending for latest first)
        const sortedTexts = thread.texts?.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        ) || [];
        
        const lastMessage = sortedTexts[0]?.content || null;
        const fromNumber = sortedTexts[0]?.from_number || null;
        
        // Count messages from the config if it exists
        let configMessageCount = 0;
        let hasConfig = false;
        
        if (thread.config) {
          hasConfig = true;
          try {
            const configData = Array.isArray(thread.config) ? thread.config : [];
            // Filter out system messages for the count
            configMessageCount = configData.filter(item => 
              (item.role === 'user' || item.role === 'assistant' || item.role === 'tool') &&
              item.role !== 'system'
            ).length;
          } catch (e) {
            console.error('Error parsing config:', e);
          }
        }
        
        // Calculate the total cost if available
        const totalCost = thread.texts?.reduce((sum, message) => {
          return sum + (Number(message.cost) || 0);
        }, 0) || 0;
        
        return {
          ...thread,
          last_message: lastMessage,
          from_number: fromNumber,
          message_count: thread.texts?.length || 0,
          config_message_count: configMessageCount,
          has_config: hasConfig,
          total_cost: totalCost
        };
      }) || [];

      // Set initial data immediately
      setTotalConversations(count || 0);
      
      // Filter out threads with zero messages and apply one-message filter if active
      const filteredThreads = processedThreads
        .filter(thread => {
          // Hide threads with zero messages
          const messageCount = thread.has_config ? thread.config_message_count : thread.message_count;
          if (messageCount === 0) return false;
          
          // Apply hide-one-message filter if active
          if (hideOneMessageThreads && messageCount === 1) return false;
          
          return true;
        });
        
      setConversations(filteredThreads);
      setAllConversations(filteredThreads);
      setIsInitialLoading(false);

      // Then fetch all threads in background if needed for search
      if (count > 10) {
        // Duplicate the query but remove the limit for background loading
        let allQuery = supabase
          .from('conversation_threads')
          .select(`
            id,
            created_at,
            updated_at,
            contact_id,
            location_id,
            node,
            config,
            node_config,
            contact:contacts(id, name, phone_number),
            texts!left(
              id,
              from_number,
              to_number,
              content,
              cost,
              created_at
            )
          `)
          .in('location_id', locationIds);
          
        // Apply the same filters for consistency
        if (showAIConversationsOnly) {
          allQuery = allQuery.not('config', 'is', null);
        }
          
        allQuery = allQuery.order('updated_at', { ascending: sortDirection === 'asc' });

        const { data: allThreads, error: allError } = await allQuery;

        if (!allError) {
          const allProcessed = allThreads?.map(thread => {
            const sortedTexts = thread.texts?.sort((a, b) => 
              new Date(b.created_at) - new Date(a.created_at)
            ) || [];
            
            const lastMessage = sortedTexts[0]?.content || null;
            const fromNumber = sortedTexts[0]?.from_number || null;
            
            // Count messages from the config if it exists
            let configMessageCount = 0;
            let hasConfig = false;
            
            if (thread.config) {
              hasConfig = true;
              try {
                const configData = Array.isArray(thread.config) ? thread.config : [];
                configMessageCount = configData.filter(item => 
                  (item.role === 'user' || item.role === 'assistant' || item.role === 'tool') &&
                  item.role !== 'system'
                ).length;
              } catch (e) {
                console.error('Error parsing config:', e);
              }
            }
            
            // Calculate the total cost if available
            const totalCost = thread.texts?.reduce((sum, message) => {
              return sum + (Number(message.cost) || 0);
            }, 0) || 0;
            
            return {
              ...thread,
              last_message: lastMessage,
              from_number: fromNumber,
              message_count: thread.texts?.length || 0,
              config_message_count: configMessageCount,
              has_config: hasConfig,
              total_cost: totalCost
            };
          }) || [];
          
          // Apply the same filters to all threads - hide zero messages and one-message if active
          const filteredAllThreads = allProcessed
            .filter(thread => {
              // Hide threads with zero messages
              const messageCount = thread.has_config ? thread.config_message_count : thread.message_count;
              if (messageCount === 0) return false;
              
              // Apply hide-one-message filter if active
              if (hideOneMessageThreads && messageCount === 1) return false;
              
              return true;
            });
          
          setAllConversations(filteredAllThreads);
        }
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setError('Failed to load conversations. Please try again.');
      setIsInitialLoading(false);
    }
  };

  const handleConversationClick = (threadId) => {
    // Set the selected thread ID for animation
    setSelectedThreadId(threadId);
    
    // Delay navigation slightly to allow for animation
    setTimeout(() => {
      navigate(`/monitor/texts/${threadId}`);
    }, 350);
  };

  // Handle filtering and pagination
  const filterAndPaginateConversations = (threads, search, status, page) => {
    let filtered = [...threads];

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(thread => 
        thread.contact?.name?.toLowerCase().includes(searchLower) ||
        thread.contact?.phone_number?.toLowerCase().includes(searchLower) ||
        thread.contact_id?.toLowerCase().includes(searchLower) ||
        thread.from_number?.toLowerCase().includes(searchLower) ||
        thread.node?.toLowerCase().includes(searchLower) ||
        thread.last_message?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter - only apply to conversations with more than 1 message
    if (status !== 'all') {
      filtered = filtered.filter(thread => {
        const messageCount = thread.has_config ? thread.config_message_count : thread.message_count;
        // Only consider conversations with more than 1 message for status filtering
        return messageCount > 1 && getConversationStatus(thread) === status;
      });
    }

    // Calculate pagination
    const total = filtered.length;
    const from = (page - 1) * conversationsPerPage;
    const to = from + conversationsPerPage;

    setTotalConversations(total);
    setConversations(filtered.slice(from, to));
  };

  // Handle search and filter changes
  useEffect(() => {
    filterAndPaginateConversations(allConversations, searchQuery, filters.status, currentPage);
  }, [searchQuery, filters.status, currentPage, allConversations]);

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      fetchInitialData();
    } catch (error) {
      console.error('Error refreshing conversations:', error);
      setError('Failed to refresh conversations. Please try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  const toggleSortDirection = () => {
    setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
  };

  const toggleHideOneMessageThreads = () => {
    setHideOneMessageThreads(prev => !prev);
    setCurrentPage(1); // Reset to first page when changing this filter
  };

  const toggleShowAIConversationsOnly = () => {
    setShowAIConversationsOnly(prev => !prev);
    setCurrentPage(1); // Reset to first page when changing this filter
  };

  const totalPages = Math.ceil(totalConversations / conversationsPerPage);

  const getConversationStatusTag = (thread) => {
    const status = getConversationStatus(thread);
    const config = statusConfig[status];
    if (!config) return null;
    
    return (
      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${config.color} ${config.bgColor}`}>
        {config.label}
      </span>
    );
  };

  // Create filter button
  const filterButton = (
    <motion.button
      variants={buttonVariants}
      initial="rest"
      whileHover="hover"
      whileTap="tap"
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-gray-200 text-gray-700 text-sm font-medium shadow-sm hover:shadow transition-all duration-200"
      aria-label="Filter conversations"
      onClick={() => setShowFilterDropdown(!showFilterDropdown)}
      aria-expanded={showFilterDropdown}
    >
      <Filter className="h-3.5 w-3.5" />
      <span>{filters.status === 'all' ? 'All Conversations' : statusConfig[filters.status]?.label || 'Filter'}</span>
    </motion.button>
  );

  // Find position of filter button to place dropdown correctly
  const filterButtonRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Update dropdown position when showing
  useEffect(() => {
    if (showFilterDropdown && filterButtonRef.current) {
      const rect = filterButtonRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth < 640;
      
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: isMobile ? null : rect.left,
        right: isMobile ? 16 : null
      });
    }
  }, [showFilterDropdown]);

  return (
    <motion.div 
      className="min-h-screen bg-gray-50"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <PageHeader
        title="Texts"
        icon={MessageCircle}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        filterButton={<div ref={filterButtonRef}>{filterButton}</div>}
        className="max-w-5xl mx-auto"
        additionalActions={
          <div className="flex items-center gap-2">
            <motion.button
              onClick={toggleShowAIConversationsOnly}
              variants={buttonVariants}
              initial="rest"
              whileHover="hover"
              whileTap="tap"
              className={`p-2.5 rounded-full ${
                showAIConversationsOnly 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-white border border-gray-200 text-gray-700'
              } shadow-sm hover:shadow transition-all duration-200`}
              aria-label={showAIConversationsOnly ? "Show all conversations" : "Show AI conversations only"}
              title={showAIConversationsOnly ? "Show all conversations" : "Show AI conversations only"}
            >
              <Bot className="h-4 w-4" />
            </motion.button>
            
            <motion.button
              onClick={toggleHideOneMessageThreads}
              variants={buttonVariants}
              initial="rest"
              whileHover="hover"
              whileTap="tap"
              className={`p-2.5 rounded-full ${
                hideOneMessageThreads 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-white border border-gray-200 text-gray-700'
              } shadow-sm hover:shadow transition-all duration-200`}
              aria-label={hideOneMessageThreads ? "Show all threads" : "Hide single message threads"}
              title={hideOneMessageThreads ? "Show all threads" : "Hide single message threads"}
            >
              <MessageCircle className="h-4 w-4" />
            </motion.button>
            
            <motion.button
              onClick={toggleSortDirection}
              variants={buttonVariants}
              initial="rest"
              whileHover="hover"
              whileTap="tap"
              className="p-2.5 rounded-full bg-white border border-gray-200 text-gray-700 shadow-sm hover:shadow transition-all duration-200"
              aria-label={`Sort by ${sortDirection === 'desc' ? 'newest' : 'oldest'} first`}
              title={`Sort by ${sortDirection === 'desc' ? 'newest' : 'oldest'} first`}
            >
              <ArrowUpDown className="h-4 w-4" />
            </motion.button>
            
            <motion.button
              onClick={handleRefresh}
              variants={buttonVariants}
              initial="rest"
              whileHover="hover"
              whileTap="tap"
              disabled={isRefreshing}
              className="p-2.5 rounded-full bg-white border border-gray-200 text-gray-700 shadow-sm hover:shadow transition-all duration-200"
              aria-label="Refresh conversations"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </motion.button>
          </div>
        }
      />

      {/* Add dropdown menu for filter options */}
      <div className="fixed inset-0 z-[9999]" 
        ref={filterDropdownRef} 
        style={{ pointerEvents: showFilterDropdown ? 'auto' : 'none' }}
        onClick={(e) => {
          // Close dropdown when clicking outside
          if (e.target === e.currentTarget) setShowFilterDropdown(false);
        }}
      >
        <AnimatePresence>
          {showFilterDropdown && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="absolute w-48 bg-white rounded-xl border border-gray-200 py-1 z-[9999] shadow-lg overflow-hidden"
              style={{ 
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                right: dropdownPosition.right
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">Status Filter</h3>
              </div>
              {/* Order: All, Active, Pending, Completed */}
              {[
                ['all', 'All Conversations'],
                ['active', statusConfig['active'].label],
                ['pending', statusConfig['pending'].label],
                ['completed', statusConfig['completed'].label]
              ].map(([value, label]) => (
                <motion.button
                  key={value}
                  onClick={() => {
                    setFilters(prev => ({ ...prev, status: value }));
                    setShowFilterDropdown(false);  // Close dropdown after selection
                  }}
                  className={`flex items-center w-full text-left px-4 py-2 text-sm ${
                    filters.status === value 
                      ? 'text-blue-600 bg-blue-50' 
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  whileHover={{ x: 2 }}
                  transition={{ duration: 0.2 }}
                >
                  {filters.status === value ? (
                    <Check className="w-4 h-4 mr-2 text-blue-600" />
                  ) : (
                    <div className="w-4 h-4 mr-2" />
                  )}
                  <span>{label}</span>
                </motion.button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="border-b border-red-100 bg-red-50"
          >
            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center text-red-600 text-sm">
              <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
              {error}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <motion.div 
          variants={cardVariants}
          className="relative"
        >
          <Card className="overflow-hidden border-0 shadow-sm bg-white">
            <CardContent className="p-0">
              {isInitialLoading ? (
                <div className="py-16 text-center">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1.5, ease: "linear", repeat: Infinity }}
                  >
                    <Loader className="w-8 h-8 text-gray-300 mx-auto" />
                  </motion.div>
                  <p className="text-sm text-gray-400 mt-4 font-light">Loading your conversations...</p>
                </div>
              ) : conversations.length === 0 ? (
                <div className="text-center py-16 sm:py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-50 flex items-center justify-center">
                    <MessageCircle className="w-8 h-8 text-gray-300" />
                  </div>
                  <h3 className="text-gray-400 text-sm font-light mb-1">
                    {searchQuery ? 'No conversations match your search' : 'No conversations yet'}
                  </h3>
                  <p className="text-gray-400 text-xs">
                    {searchQuery ? 'Try adjusting your filters' : 'Your text conversations will appear here'}
                  </p>
                </div>
              ) : (
                <AnimatePresence>
                  <div>
                    <div className="divide-y divide-gray-100">
                      {conversations.map((thread, index) => (
                        <motion.div
                          layout
                          key={thread.id}
                          variants={listItemVariants}
                          initial="initial"
                          animate="animate"
                          whileHover="hover"
                          whileTap="tap"
                          custom={index}
                          onClick={() => handleConversationClick(thread.id)}
                          className={`py-4 sm:py-5 px-4 sm:px-6 cursor-pointer transition-all duration-300 ${
                            selectedThreadId === thread.id ? 'bg-blue-50' : ''
                          } ${thread.has_config ? 'border-l-2 border-blue-300' : ''}`}
                        >
                          <div className="flex items-center gap-3 sm:gap-5">
                            {/* Status indicator with AI detection */}
                            <div className="relative flex-shrink-0">
                              <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full ${
                                // Check if only 1 message first - always gray in that case
                                (thread.has_config ? thread.config_message_count : thread.message_count) === 1
                                  ? 'bg-gray-50 text-gray-400'
                                  : thread.has_config 
                                    ? 'bg-blue-50 text-blue-600'
                                    : getConversationStatus(thread) === 'active' 
                                      ? 'bg-blue-50 text-blue-600'
                                      : 'bg-gray-50 text-gray-400'
                              } flex items-center justify-center`}>
                                {thread.has_config ? (
                                  <Bot className="w-4 h-4" />
                                ) : (
                                  <User className="w-4 h-4" />
                                )}
                              </div>
                              {/* Only show active blue dot for conversations with more than 1 message */}
                              {getConversationStatus(thread) === 'active' && 
                               ((thread.has_config ? thread.config_message_count : thread.message_count) > 1) && (
                                <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-blue-500 border-2 border-white" />
                              )}
                            </div>
                            
                            {/* Conversation information */}
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                                <div className="truncate">
                                  <span className="font-medium text-gray-900">
                                    {getDisplayName(thread)}
                                  </span>
                                </div>
                                
                                {/* Conversation date with icon */}
                                <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1 sm:mt-0">
                                  <Clock className="w-3 h-3" />
                                  <span>{formatConversationDate(thread.updated_at || thread.created_at)}</span>
                                </div>
                              </div>
                              
                              {/* Message preview */}
                              <div className="mt-1 text-sm text-gray-600 truncate">
                                {getLastMessagePreview(thread)}
                              </div>
                              
                              <div className="mt-1 flex flex-wrap items-center gap-2 sm:gap-3">
                                {/* Status tag - only show for conversations with more than 1 message */}
                                {((thread.has_config ? thread.config_message_count : thread.message_count) > 1) && getConversationStatusTag(thread)}
                                
                                {/* Display message count */}
                                <span className="text-xs text-gray-500 bg-gray-50 py-0.5 px-2 rounded-full">
                                  {thread.has_config 
                                    ? `${thread.config_message_count} AI messages` 
                                    : `${thread.message_count} messages`}
                                </span>
                              </div>
                            </div>
                            
                            {/* Right arrow indicator */}
                            <motion.div 
                              animate={{ x: selectedThreadId === thread.id ? 5 : 0 }}
                              transition={{ duration: 0.3 }}
                              className="flex-shrink-0"
                            >
                              <ChevronRight className="h-5 w-5 text-gray-300" />
                            </motion.div>
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    {/* Pagination - elegant design and mobile-friendly */}
                    {totalConversations > conversationsPerPage && (
                      <div className="p-4 border-t border-gray-100 bg-gray-50 bg-opacity-50">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
                          <p className="text-xs text-gray-500 font-light order-2 sm:order-1 text-center sm:text-left">
                            Showing {((currentPage - 1) * conversationsPerPage) + 1} to {Math.min(currentPage * conversationsPerPage, totalConversations)} of {totalConversations} conversations
                          </p>
                          <div className="flex items-center justify-center sm:justify-end gap-3 order-1 sm:order-2">
                            <motion.button
                              variants={buttonVariants}
                              initial="rest"
                              whileHover="hover"
                              whileTap="tap"
                              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                              disabled={currentPage === 1}
                              className={`p-2 rounded-full border ${
                                currentPage === 1 
                                  ? 'border-gray-200 text-gray-300 cursor-not-allowed' 
                                  : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:shadow-sm'
                              }`}
                              aria-label="Previous page"
                            >
                              <ChevronLeft className="w-4 h-4" />
                            </motion.button>
                            
                            <span className="text-sm font-medium text-gray-700 min-w-[40px] text-center">
                              {currentPage} / {totalPages}
                            </span>
                            
                            <motion.button
                              variants={buttonVariants}
                              initial="rest"
                              whileHover="hover"
                              whileTap="tap"
                              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                              disabled={currentPage === totalPages}
                              className={`p-2 rounded-full border ${
                                currentPage === totalPages 
                                  ? 'border-gray-200 text-gray-300 cursor-not-allowed' 
                                  : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:shadow-sm'
                              }`}
                              aria-label="Next page"
                            >
                              <ChevronRight className="w-4 h-4" />
                            </motion.button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </AnimatePresence>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default TextsListPage; 